/**
 * BhiReferralWidget.jsx — iter668
 *
 * Показва се само след първия upload (sources_used.length ≥ 1). Дава на
 * пациента 1-click share към WhatsApp / Viber / Telegram / Email + copy link.
 * Записва всеки share event в backend за counters.
 *
 * Strict Bulgarian copy. No tracking of friends' data — само counters.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Gift, Send, Mail, Link2, Check, Heart, Users,
} from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";

// Inline brand glyphs (no external libs needed)
const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.198.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413"/></svg>
);
const ViberIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}><path d="M11.4 0C9.5.1 5.4.4 3.1 2.5 1.4 4.2.8 6.6.7 9.7c-.1 3 0 7 4 8.1V20s0 .7.5.9c.5.2.8-.3 1.2-.7l1.9-2.1c2.8.2 5-.4 5.2-.4 1.4-.4 8.6-1.4 9.6-9.6.9-8.4-7.1-9-7.6-9-1.7-.1-3.6 0-5 0M7.4 5.7c.8 0 1.6.1 2.3.6.5.4 1 1 1.1 1.5.1.4 0 .9-.4 1.1-.2.1-.3.2-.5.3-.2.1-.5.1-.6.4-.1.2 0 .5.1.7.4.7 1 1.3 1.7 1.7.2.1.4.2.7.1.2-.1.3-.3.5-.5.2-.2.4-.4.7-.5.4-.1.7 0 1 .2.6.5 1.2 1 1.6 1.6.2.4.1.9-.2 1.2-.4.4-1.1.8-1.7.8-1.5 0-3-.7-4.3-1.7-1.3-1-2.4-2.4-3-3.9-.2-.5-.4-1.1-.4-1.6 0-.6.3-1.3.9-1.7.2-.1.4-.2.7-.2"/></svg>
);
const TelegramIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
);

const TEMPLATES_BG = {
  default: "Току-що открих как да оптимизирам метаболитния си профил с Breast Health Intelligence — персонализиран план базиран на моите DUTCH/ДНК и серумни хормонални тестове. Препоръчвам го от сърце 💗",
  short:   "Препоръчвам Ви Breast Health Intelligence — образователен AI помощник за гръдното здраве (DUTCH, ДНК, лаборатории, персонализиран план).",
  email:   "Здравей,\n\nИскам да споделя с теб инструмент, който намерих много полезен — Breast Health Intelligence. Това е AI-помощник, който качва Вашите лабораторни изследвания (DUTCH, ДНК, серумни хормони) и създава персонализиран план за оптимизация на гръдното здраве.\n\nНе е диагностика — образователен е и помага да разбереш биологичния си пейзаж и какво можеш да повлияеш.\n\nПрепоръчвам го от сърце.",
};

export default function BhiReferralWidget({ sourcesUsed: sourcesUsedProp }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const [sourcesUsed, setSourcesUsed] = useState(sourcesUsedProp || null);

  // Self-fetch sources_used if not provided as prop
  useEffect(() => {
    if (Array.isArray(sourcesUsedProp)) {
      setSourcesUsed(sourcesUsedProp);
      return;
    }
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/bhi/shared-context`);
        if (active) setSourcesUsed(data?.sources_used || []);
      } catch {
        if (active) setSourcesUsed([]);
      }
    })();
    return () => { active = false; };
  }, [API, sourcesUsedProp]);

  const shouldShow = Array.isArray(sourcesUsed) && sourcesUsed.length >= 1;

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/bhi/referral/info`);
      setInfo(data);
    } catch { /* silent */ }
  }, [API]);

  useEffect(() => {
    if (shouldShow) load();
  }, [shouldShow, load]);

  const recordShare = useCallback(async (channel) => {
    try {
      const { data } = await axios.post(`${API}/bhi/referral/share`, { channel });
      setInfo((p) => p ? { ...p, share_count: data.share_count, by_channel: { ...(p.by_channel || {}), [channel]: (p.by_channel?.[channel] || 0) + 1 } } : p);
    } catch { /* silent — share still opens */ }
  }, [API]);

  if (!shouldShow) return null;

  const appUrl = (() => {
    try { return window.location.origin; } catch { return ""; }
  })();
  const shareUrl = `${appUrl}/?ref=${info?.code || ""}`;
  const msg = TEMPLATES_BG.default + " " + shareUrl;
  const enc = encodeURIComponent;

  const handlers = {
    whatsapp: () => {
      recordShare("whatsapp");
      window.open(`https://wa.me/?text=${enc(msg)}`, "_blank", "noopener,noreferrer");
    },
    viber: () => {
      recordShare("viber");
      window.open(`viber://forward?text=${enc(msg)}`, "_blank", "noopener,noreferrer");
    },
    telegram: () => {
      recordShare("telegram");
      window.open(`https://t.me/share/url?url=${enc(shareUrl)}&text=${enc(TEMPLATES_BG.short)}`, "_blank", "noopener,noreferrer");
    },
    email: () => {
      recordShare("email");
      const subject = enc("Препоръчвам Ви Breast Health Intelligence");
      const body = enc(TEMPLATES_BG.email + "\n\n" + shareUrl);
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    },
    copy: async () => {
      recordShare("copy");
      try {
        await navigator.clipboard.writeText(msg);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch { /* ignore */ }
    },
  };

  const cardCls = `rounded-2xl border ${isDark ? "bg-[hsl(222,47%,11%)] border-pink-500/20" : "bg-white border-pink-200"}`;

  const buttonBase = `flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold transition-all hover:scale-[1.02] hover:shadow-md`;

  return (
    <div className={`${cardCls} p-5 mt-4`} data-testid="bhi-referral-widget">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-pink-500 via-rose-500 to-purple-500 flex items-center justify-center shrink-0">
          <Gift className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <h4 className="text-base font-extrabold">💝 Помогнете на приятелка</h4>
          <p className={`text-xs mt-0.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
            Споделете BHI с жени, които биха се възползвали — превенцията е по-силна, когато е общност.
          </p>
        </div>
      </div>

      <div className={`p-3 rounded-xl mb-3 text-xs italic leading-relaxed ${isDark ? "bg-white/[0.03] text-gray-300" : "bg-pink-50/60 text-gray-700"}`} data-testid="referral-preview">
        „{TEMPLATES_BG.default}“
      </div>

      <div className="flex flex-wrap gap-2" data-testid="referral-channels">
        <button
          onClick={handlers.whatsapp}
          className={`${buttonBase} bg-[#25D366] text-white hover:bg-[#1ebe57]`}
          data-testid="referral-whatsapp"
        >
          <WhatsAppIcon width={14} height={14} /> WhatsApp
        </button>
        <button
          onClick={handlers.viber}
          className={`${buttonBase} bg-[#7360F2] text-white hover:bg-[#5a48d6]`}
          data-testid="referral-viber"
        >
          <ViberIcon width={14} height={14} /> Viber
        </button>
        <button
          onClick={handlers.telegram}
          className={`${buttonBase} bg-[#0088cc] text-white hover:bg-[#0070a8]`}
          data-testid="referral-telegram"
        >
          <TelegramIcon width={14} height={14} /> Telegram
        </button>
        <button
          onClick={handlers.email}
          className={`${buttonBase} ${isDark ? "bg-white/10 text-white hover:bg-white/15" : "bg-gray-200 text-gray-800 hover:bg-gray-300"}`}
          data-testid="referral-email"
        >
          <Mail className="w-3.5 h-3.5" /> Email
        </button>
        <button
          onClick={handlers.copy}
          className={`${buttonBase} ${
            copied
              ? "bg-emerald-500 text-white"
              : (isDark ? "bg-white/10 text-white hover:bg-white/15" : "bg-gray-200 text-gray-800 hover:bg-gray-300")
          }`}
          data-testid="referral-copy"
        >
          {copied ? <><Check className="w-3.5 h-3.5" /> Копирано</> : <><Link2 className="w-3.5 h-3.5" /> Копирай</>}
        </button>
      </div>

      {info?.share_count > 0 && (
        <div className={`mt-3 inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full ${
          isDark ? "bg-pink-500/10 text-pink-300" : "bg-pink-100 text-pink-700"
        }`} data-testid="referral-count">
          <Heart className="w-3 h-3 fill-current" />
          {info.share_count === 1
            ? "Споделихте 1 път — благодарим Ви! 💗"
            : `Споделихте ${info.share_count} пъти — благодарим Ви! 💗`}
        </div>
      )}

      <p className="text-[10px] opacity-50 italic mt-3">
        Никакви данни на Вашите приятели не се събират — споделяте САМО линка към BHI.
      </p>
    </div>
  );
}
